import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Plus, Trash2, Pencil, PowerOff, Power, Repeat, Clock, MapPin, List, LayoutGrid, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useAutoRefresh from "@/lib/useAutoRefresh";
import AppointmentDialog from "@/components/AppointmentDialog";
import AppointmentsWeekView from "@/components/AppointmentsWeekView";
import AppointmentsTrash from "@/components/AppointmentsTrash";
import { useAuth } from "@/context/AuthContext";

const DOW_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const fmtNextOcc = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      timeZone: "America/New_York",
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

export default function Appointments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  // Persist the last picked view mode so a dispatcher's preference sticks
  // across visits. Falls back to "list" when localStorage is unavailable.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("appt.view") || "list"; } catch { return "list"; }
  });
  const setViewPersisted = (v) => {
    setView(v);
    try { localStorage.setItem("appt.view", v); } catch { /* private mode: skip */ }
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/appointments");
      setRows(data);
    } catch (e) { if (!silent) toast.error(formatApiError(e)); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh so newly-dropped occurrences update the badges.
  useAutoRefresh(() => load({ silent: true }), 20000);

  const remove = async (a) => {
    if (!window.confirm(
      `¿Eliminar la cita de ${a.passenger_name || "sin nombre"} ${a.weekdays?.length ? "(recurrente, TODA la serie)" : "(única)"}?\n\n` +
      "Va a la papelera: un admin puede restaurarla. Los viajes que ya bajaron al panel siguen activos."
    )) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Cita movida a la papelera");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const skipDay = async (a, ymd) => {
    try {
      await api.post(`/appointments/${a.id}/skip-day`, { date: ymd });
      toast.success("Día quitado; la serie sigue igual");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unskipDay = async (a, ymd) => {
    try {
      await api.post(`/appointments/${a.id}/unskip-day`, { date: ymd });
      toast.success("Día restaurado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (a) => {
    try {
      await api.patch(`/appointments/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Cita pausada" : "Cita activada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Force the next occurrence to drop into Dispatch right now. The backend
  // marks that occurrence as dropped so the scheduler will NOT try to drop
  // it again today. Future recurrences follow their normal schedule.
  const dropNow = async (a) => {
    if (!window.confirm(
      `¿Bajar ahora al panel de Dispatch la próxima ocurrencia de ${a.passenger_name || "esta cita"}?\n\n` +
      "Ese día ya no bajará automáticamente. La próxima programación quedará igual."
    )) return;
    try {
      await api.post(`/appointments/${a.id}/drop-now`);
      toast.success("Cita bajada al panel");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sorted = useMemo(
    () => [...rows].sort((x, y) => (x.next_occurrence || "").localeCompare(y.next_occurrence || "") || (y.active - x.active)),
    [rows],
  );

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-primary flex items-center gap-2">
              <CalendarClock className="w-3 h-3" /> AGENDA
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Citas programadas
            </h1>
            <p className="text-xs text-muted-foreground">
              Las citas caen automáticamente al panel de Dispatch <b>N minutos antes</b> de su hora.
              Se puede repetir por día de la semana o programar una sola vez.
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setOpen(true); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
            data-testid="new-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Nueva cita
          </Button>
        </div>

        {/* View toggle: list (rows) vs week grid (calendar). */}
        <div className="flex justify-end">
          <div className="inline-flex rounded-md border border-border bg-card p-0.5" data-testid="appt-view-toggle">
            <button
              type="button" onClick={() => setViewPersisted("list")}
              data-testid="appt-view-list"
              className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 transition-all ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"}`}
            >
              <List className="w-3.5 h-3.5" /> Lista
            </button>
            <button
              type="button" onClick={() => setViewPersisted("week")}
              data-testid="appt-view-week"
              className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 transition-all ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Semana
            </button>
            {isAdmin && (
              <button
                type="button" onClick={() => setViewPersisted("trash")}
                data-testid="appt-view-trash"
                className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 transition-all ${view === "trash" ? "bg-red-500/80 text-white" : "text-muted-foreground hover:text-white"}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Papelera
              </button>
            )}
          </div>
        </div>

        {view === "trash" && isAdmin ? (
          <AppointmentsTrash onRestored={() => load({ silent: true })} />
        ) : view === "week" ? (
          <AppointmentsWeekView
            appointments={rows}
            onEdit={(a) => { setEditing(a); setOpen(true); }}
            onToggle={toggle}
            onDropNow={dropNow}
            onDelete={isAdmin ? remove : undefined}
            onSkipDay={skipDay}
            onUnskipDay={unskipDay}
          />
        ) : loading ? (
          <Card className="p-8 text-muted-foreground text-center bg-card border-border">Cargando…</Card>
        ) : sorted.length === 0 ? (
          <Card className="p-10 text-center bg-card border-border">
            <CalendarClock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <div className="text-muted-foreground">Aún no has creado citas.</div>
            <div className="text-xs text-muted-foreground mt-1">Haz clic en <b>Nueva cita</b> para empezar.</div>
          </Card>
        ) : (
          <div className="grid gap-3">
            {sorted.map((a) => (
              <Card
                key={a.id}
                data-testid={`appt-card-${a.id}`}
                className={`p-4 bg-card border-border transition-all ${a.active ? "" : "opacity-60"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[220px] space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-white">{a.passenger_name || <span className="text-muted-foreground">Sin nombre</span>}</span>
                      {a.passenger_phone && <span className="text-xs font-mono text-muted-foreground">{a.passenger_phone}</span>}
                      {a.weekdays?.length > 0 ? (
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 gap-1"><Repeat className="w-3 h-3" /> semanal</Badge>
                      ) : (
                        <Badge variant="outline" className="border-sky-500/40 text-sky-300 gap-1"><Clock className="w-3 h-3" /> única</Badge>
                      )}
                      {!a.active && <Badge variant="outline" className="border-red-500/40 text-red-300">pausada</Badge>}
                    </div>

                    <div className="text-xs text-white/80 leading-snug">
                      <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-primary" /> {a.pickup_address || "—"}</div>
                      <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-accent" /> {a.dropoff_address || "—"}</div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap text-xs font-mono">
                      <span className="text-primary font-bold">
                        {String(a.hour).padStart(2, "0")}:{String(a.minute).padStart(2, "0")}
                      </span>
                      {a.weekdays?.length > 0 && (
                        <span className="flex items-center gap-1 flex-wrap">
                          {DOW_LABELS.map((lbl, idx) => (
                            <span
                              key={idx}
                              className={`px-1.5 py-0.5 rounded ${a.weekdays.includes(idx) ? "bg-primary/20 text-primary" : "text-muted-foreground/40"}`}
                            >
                              {lbl}
                            </span>
                          ))}
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        bajar {a.lead_minutes}′ antes
                      </span>
                      {a.price > 0 && <span className="text-amber-400 font-bold">${a.price.toFixed(2)}</span>}
                    </div>

                    <div className="text-[10px] font-mono text-muted-foreground" data-testid={`appt-next-${a.id}`}>
                      Próxima: <span className="text-white/80">{fmtNextOcc(a.next_occurrence)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline" size="sm" onClick={() => dropNow(a)}
                      className="border-amber-500/40 h-8 px-2 text-amber-300 hover:bg-amber-500/10 font-bold text-xs"
                      data-testid={`appt-dropnow-${a.id}`}
                      title="Bajar esta cita al panel de Dispatch ahora"
                      disabled={!a.active}
                    >
                      <Zap className="w-3.5 h-3.5 mr-0.5" /> Bajar ahora
                    </Button>
                    <Button
                      variant="outline" size="sm" onClick={() => toggle(a)}
                      className="border-border h-8 w-8 p-0"
                      data-testid={`appt-toggle-${a.id}`}
                      title={a.active ? "Pausar (deja de bajar)" : "Reactivar"}
                    >
                      {a.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5 text-emerald-400" />}
                    </Button>
                    <Button
                      variant="outline" size="sm" onClick={() => { setEditing(a); setOpen(true); }}
                      className="border-border h-8 w-8 p-0"
                      data-testid={`appt-edit-${a.id}`} title="Editar cita"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline" size="sm" onClick={() => remove(a)}
                        className="border-red-500/40 h-8 w-8 p-0 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        data-testid={`appt-delete-${a.id}`} title="Eliminar cita"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AppointmentDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        appointment={editing}
        onSaved={() => { setOpen(false); setEditing(null); load(); }}
      />
    </AppLayout>
  );
}
